import React from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { toast } from '@/components/ui/use-toast';
import { useSupabaseStore } from '@/store/SupabaseStore';
import ChatFavoriteButton from './ChatFavoriteButton';
import ChatDeleteButton from './ChatDeleteButton';
import ChatContentTypeBadge from './ChatContentTypeBadge';
import ChatPreviewContent from './ChatPreviewContent';
import ChatFooterStats from './ChatFooterStats';
import { getChatContentType, getPreviewContent, countAttachmentTypes } from '@/utils/chatContentUtils';
import AssignToProjectButton from '../projects/AssignToProjectButton';
import { Button } from '@/components/ui/button';
import { FolderX } from 'lucide-react';

interface ChatCardProps {
  id: string;
  title: string;
  updatedAt: string;
  viewCount: number;
  tokensUsed: number;
  isFavorite: boolean;
  messages: any[];
  loadingMessages?: boolean;
  messageCount?: number;
  project_id?: string | null;
  onOpenChat?: (id: string, title?: string) => void;
}

const ChatCard = ({
  id,
  title,
  updatedAt,
  viewCount,
  tokensUsed,
  isFavorite,
  messages,
  loadingMessages,
  messageCount,
  project_id,
  onOpenChat,
}: ChatCardProps) => {
  const navigate = useNavigate();
  const { toggleFavoriteChat, deleteChat, assignChatToProject } = useSupabaseStore();

  const safeMessages = Array.isArray(messages) ? messages : [];

  const handleCardClick = () => {
    if (onOpenChat) {
      onOpenChat(id, title);
      return;
    }

    navigate(`/chat/${id}`);
  };

  const handleToggleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await toggleFavoriteChat(id);
      toast({
        title: isFavorite ? 'Removed from favorites' : 'Added to favorites',
        description: isFavorite
          ? 'The chat has been removed from your favorites.'
          : 'The chat has been added to your favorites.',
      });
    } catch (error) {
      console.error('Error toggling favorite status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update favorite status. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteChat = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await deleteChat(id);
      toast({
        title: 'Chat deleted',
        description: 'The chat has been successfully deleted.',
      });
    } catch (error) {
      console.error('Error deleting chat:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete the chat. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleRemoveFromProject = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await assignChatToProject(id, null);
      toast({
        title: 'Chat removed from project',
        description: 'The chat has been successfully unassigned from this project.',
      });
    } catch (error) {
      console.error('Error removing chat from project:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove chat from project. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const contentType = getChatContentType(safeMessages);
  const preview = getPreviewContent(safeMessages);
  const { imageCount, codeCount, canvasCount } = countAttachmentTypes(safeMessages);

  let lastUpdated = 'unknown';
  try {
    if (updatedAt && !isNaN(Date.parse(updatedAt))) {
      lastUpdated = formatDistanceToNow(new Date(updatedAt), { addSuffix: true });
    }
  } catch {
    lastUpdated = 'unknown';
  }

  const totalMessageCount = typeof messageCount === 'number' ? messageCount : safeMessages.length;

  return (
    <Card
      className={cn(
        'relative cursor-pointer overflow-hidden transition-all hover:shadow-md',
        isFavorite && 'border-primary'
      )}
      onClick={handleCardClick}
    >
      <CardHeader className="relative p-6 pb-6">
        <div className="absolute right-3 top-1 flex space-x-1">
          {project_id && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRemoveFromProject}
              className="h-8 w-8"
            >
              <FolderX className="h-4 w-4" />
            </Button>
          )}
          <AssignToProjectButton chatId={id} currentProjectId={project_id} />
          <ChatFavoriteButton isFavorite={isFavorite} onToggleFavorite={handleToggleFavorite} />
          <ChatDeleteButton onDeleteChat={handleDeleteChat} />
        </div>

        <ChatContentTypeBadge contentType={contentType} />

        {preview.thumbnailUrl && (
          <div className="absolute left-4 top-4 h-8 w-8 overflow-hidden rounded-md">
            <img
              src={preview.thumbnailUrl}
              alt=""
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </div>
        )}

        <CardTitle
          className={cn('mt-1 truncate text-lg', preview.thumbnailUrl ? 'pl-10' : '')}
        >
          {title || 'Untitled Chat'}
        </CardTitle>

        <p
          className={cn(
            'mt-1 text-xs text-muted-foreground',
            preview.thumbnailUrl ? 'pl-10' : ''
          )}
        >
          {lastUpdated}
        </p>
      </CardHeader>

      <CardContent className="p-4 pt-2">
        <ChatPreviewContent preview={preview} loading={loadingMessages} />
      </CardContent>

      <CardFooter className="flex items-center justify-between p-4 pt-0">
        <ChatFooterStats
          viewCount={viewCount}
          tokensUsed={tokensUsed}
          imageCount={imageCount}
          codeCount={codeCount}
          canvasCount={canvasCount}
          messageCount={totalMessageCount}
        />
      </CardFooter>
    </Card>
  );
};

export default ChatCard;
